import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import { tenantsApi } from '@/modules/system/tenants/api/tenantsApi'
import type { Tenant } from '@/modules/system/tenants/api/tenantsApi'
import { organizationsApi } from '@/modules/system/organizations/api/organizationsApi'
import { departmentsApi } from '@/modules/organization/departments/api/departmentsApi'
import { rolesApi } from '@/modules/organization/roles/api/rolesApi'

type Stats = {
  tenant: Tenant | null
  organizationsCount: number
  departmentsCount: number
  rolesCount: number
  loading: boolean
}

export default function MyOrganizationPage() {
  const tenantId = useActiveTenantId()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [stats, setStats] = useState<Stats>({
    tenant: null,
    organizationsCount: 0,
    departmentsCount: 0,
    rolesCount: 0,
    loading: true,
  })
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState<Partial<Tenant>>({})
  const [expandDetails, setExpandDetails] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!tenantId) {
      setStats((s) => ({ ...s, tenant: null, loading: false }))
      return
    }
    setStats((s) => ({ ...s, loading: true }))
    setError(null)
    try {
      const [tenantRes, orgRes, deptRes, rolesRes] = await Promise.all([
        tenantsApi.getById(tenantId),
        organizationsApi.list({ tenantId, pageSize: 1, page: 1, isActive: true }),
        departmentsApi.list({ tenantId, pageSize: 1, page: 1, isActive: true }),
        rolesApi.list({ tenantId, pageSize: 1, page: 1 }),
      ])
      const tenant = tenantRes
      setStats({
        tenant,
        organizationsCount: orgRes.totalCount ?? 0,
        departmentsCount: deptRes.totalCount ?? 0,
        rolesCount: rolesRes.totalCount ?? 0,
        loading: false,
      })
      setFormData({
        name: tenant.name,
        description: tenant.description ?? undefined,
        legalName: tenant.legalName ?? undefined,
        taxNumber: tenant.taxNumber ?? undefined,
        taxOffice: tenant.taxOffice ?? undefined,
        industry: tenant.industry ?? undefined,
        organizationType: tenant.organizationType ?? undefined,
        foundationDate: tenant.foundationDate ?? undefined,
        address: tenant.address ?? undefined,
        country: tenant.country ?? undefined,
        city: tenant.city ?? undefined,
        phone: tenant.phone ?? undefined,
        email: tenant.email ?? undefined,
        website: tenant.website ?? undefined,
        authorizedPersonName: tenant.authorizedPersonName ?? undefined,
      })
    } catch (e: any) {
      toast({ title: 'Hata', description: e?.message ?? 'Veri yüklenemedi', variant: 'danger' })
      setError(e?.message ?? 'Veri yüklenemedi')
      setStats((s) => ({ ...s, loading: false }))
    }
  }, [tenantId, toast])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = async () => {
    if (!tenantId || !stats.tenant) return
    setSaveLoading(true)
    setError(null)
    try {
      await tenantsApi.update({
        id: tenantId,
        name: formData.name ?? stats.tenant.name,
        description: formData.description ?? null,
        legalName: formData.legalName ?? null,
        taxNumber: formData.taxNumber ?? null,
        taxOffice: formData.taxOffice ?? null,
        industry: formData.industry ?? null,
        organizationType: formData.organizationType ?? null,
        foundationDate: formData.foundationDate ?? null,
        address: formData.address ?? null,
        country: formData.country ?? null,
        city: formData.city ?? null,
        phone: formData.phone ?? null,
        email: formData.email ?? null,
        website: formData.website ?? null,
        authorizedPersonName: formData.authorizedPersonName ?? null,
        status: stats.tenant.status,
      })
      toast({ title: 'Başarılı', description: 'Organizasyon bilgileri güncellendi', variant: 'success' })
      setEditMode(false)
      await load()
    } catch (e: any) {
      setError(e?.message ?? 'Kayıt işlemi başarısız')
      toast({ title: 'Hata', description: e?.message ?? 'Kayıt işlemi başarısız', variant: 'danger' })
    } finally {
      setSaveLoading(false)
    }
  }

  const tenant = stats.tenant

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Organizasyonum"
        subtitle="Tenant özeti, bilgiler ve istatistikler"
        actions={
          <Button variant="outline" size="sm" onClick={load} leftIcon={<i className="material-symbols-outlined !text-lg">refresh</i>}>
            Yenile
          </Button>
        }
      />

      {stats.loading ? (
        <div className="mt-6 space-y-6">
          <Card className="p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </Card>
            ))}
          </div>
        </div>
      ) : !tenantId ? (
        <Card className="p-6 mt-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <i className="material-symbols-outlined !text-2xl">business</i>
            <p>Tenant seçilmedi. Üst menüden tenant seçin.</p>
          </div>
        </Card>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Organizasyon Bilgileri Kartı */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <i className="material-symbols-outlined text-primary-600">apartment</i>
                Organizasyon Bilgileri
              </CardTitle>
              {!editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)} leftIcon={<i className="material-symbols-outlined !text-lg">edit</i>}>
                  Düzenle
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="danger" className="mb-4" onDismiss={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}

              {editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Organizasyon Adı"
                    value={formData.name ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                  />
                  <Input
                    label="Ticari Unvan"
                    value={formData.legalName ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, legalName: e.target.value }))}
                  />
                  <Input
                    label="Vergi Numarası"
                    value={formData.taxNumber ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, taxNumber: e.target.value }))}
                  />
                  <Input
                    label="Vergi Dairesi"
                    value={formData.taxOffice ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, taxOffice: e.target.value }))}
                  />
                  <Input
                    label="Sektör / Faaliyet Alanı"
                    value={formData.industry ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, industry: e.target.value }))}
                  />
                  <Input
                    label="Telefon"
                    value={formData.phone ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
                  />
                  <Input
                    label="E-posta"
                    type="email"
                    value={formData.email ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                  />
                  <Input
                    label="Web Sitesi"
                    value={formData.website ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, website: e.target.value }))}
                  />
                  <Input
                    label="Yetkili Kişi"
                    value={formData.authorizedPersonName ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, authorizedPersonName: e.target.value }))}
                  />
                  <Input
                    label="Ülke"
                    value={formData.country ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, country: e.target.value }))}
                  />
                  <Input
                    label="İl / Şehir"
                    value={formData.city ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, city: e.target.value }))}
                  />
                  <Input
                    label="Kuruluş Tarihi"
                    type="date"
                    value={formData.foundationDate ?? ''}
                    onChange={(e) => setFormData((f) => ({ ...f, foundationDate: e.target.value }))}
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="Resmi Adres"
                      value={formData.address ?? ''}
                      onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Textarea
                      label="Açıklama"
                      value={formData.description ?? ''}
                      onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className="md:col-span-2 flex gap-2">
                    <Button onClick={handleSave} loading={saveLoading} disabled={saveLoading}>
                      Kaydet
                    </Button>
                    <Button variant="outline" onClick={() => setEditMode(false)} disabled={saveLoading}>
                      İptal
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Organizasyon Adı</p>
                      <p className="text-sm font-semibold">{tenant?.name ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Ticari Unvan</p>
                      <p className="text-sm font-semibold">{tenant?.legalName ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Sektör</p>
                      <p className="text-sm font-semibold">{tenant?.industry ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Telefon</p>
                      <p className="text-sm font-semibold">{tenant?.phone ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">E-posta</p>
                      <p className="text-sm font-semibold">{tenant?.email ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Durum</p>
                      <Badge variant={tenant?.status === 'active' ? 'success' : 'warning'}>{tenant?.status ?? '—'}</Badge>
                    </div>
                  </div>

                  <Button variant="ghost" size="sm" onClick={() => setExpandDetails((e) => !e)}>
                    {expandDetails ? (
                      <>
                        <i className="material-symbols-outlined !text-base">expand_less</i>
                        Detayları Gizle
                      </>
                    ) : (
                      <>
                        <i className="material-symbols-outlined !text-base">expand_more</i>
                        Tüm Detayları Göster
                      </>
                    )}
                  </Button>

                  {expandDetails && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Vergi Numarası</p>
                          <p className="text-sm font-semibold">{tenant?.taxNumber ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Vergi Dairesi</p>
                          <p className="text-sm font-semibold">{tenant?.taxOffice ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Kuruluş Tipi</p>
                          <p className="text-sm font-semibold">{tenant?.organizationType ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Kuruluş Tarihi</p>
                          <p className="text-sm font-semibold">
                            {tenant?.foundationDate ? new Date(tenant.foundationDate).toLocaleDateString('tr-TR') : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Yetkili Kişi</p>
                          <p className="text-sm font-semibold">{tenant?.authorizedPersonName ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Web Sitesi</p>
                          <p className="text-sm font-semibold">
                            {tenant?.website ? (
                              <a href={tenant.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                                Ziyaret Et
                              </a>
                            ) : (
                              '—'
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Ülke</p>
                          <p className="text-sm font-semibold">{tenant?.country ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">İl / Şehir</p>
                          <p className="text-sm font-semibold">{tenant?.city ?? '—'}</p>
                        </div>
                      </div>
                      <div className="md:col-span-1">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Resmi Adres</p>
                          <p className="text-sm font-semibold">{tenant?.address ?? '—'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* İstatistik Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">Organizasyon</p>
                <i className="material-symbols-outlined text-primary-600 !text-2xl">apartment</i>
              </div>
              <p className="text-2xl font-bold">{tenant?.name ?? '—'}</p>
              <p className="text-sm text-muted-foreground mt-1">Tenant adı</p>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">İşyeri Sayısı</p>
                <i className="material-symbols-outlined text-success-600 !text-2xl">location_on</i>
              </div>
              <p className="text-2xl font-bold">{stats.organizationsCount}</p>
              <p className="text-sm text-muted-foreground mt-1">Toplam işyeri</p>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">Departman Sayısı</p>
                <i className="material-symbols-outlined text-purple-600 !text-2xl">business_center</i>
              </div>
              <p className="text-2xl font-bold">{stats.departmentsCount}</p>
              <p className="text-sm text-muted-foreground mt-1">Toplam departman</p>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">Rol Sayısı</p>
                <i className="material-symbols-outlined text-info-600 !text-2xl">shield</i>
              </div>
              <p className="text-2xl font-bold">{stats.rolesCount}</p>
              <p className="text-sm text-muted-foreground mt-1">Toplam rol</p>
            </Card>
          </div>

          {/* Hızlı İşlemler */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="material-symbols-outlined text-primary-600">bolt</i>
                Hızlı İşlemler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start text-left"
                  onClick={() => navigate('/organization/workplaces')}
                >
                  <span className="flex items-center gap-2 font-semibold mb-1">
                    <i className="material-symbols-outlined text-success-600">location_on</i>
                    İşyeri Ekle
                  </span>
                  <span className="text-sm text-muted-foreground">Yeni işyeri konumu tanımla</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start text-left"
                  onClick={() => navigate('/organization/departments')}
                >
                  <span className="flex items-center gap-2 font-semibold mb-1">
                    <i className="material-symbols-outlined text-purple-600">business_center</i>
                    Departman Oluştur
                  </span>
                  <span className="text-sm text-muted-foreground">Yeni departman yapısı kur</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start text-left"
                  onClick={() => navigate('/organization/roles')}
                >
                  <span className="flex items-center gap-2 font-semibold mb-1">
                    <i className="material-symbols-outlined text-info-600">shield</i>
                    Rol Yönetimi
                  </span>
                  <span className="text-sm text-muted-foreground">Rol ve yetki tanımları</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start text-left"
                  onClick={() => navigate('/organization/org-tree')}
                >
                  <span className="flex items-center gap-2 font-semibold mb-1">
                    <i className="material-symbols-outlined text-warning-600">account_tree</i>
                    Organizasyon Ağacı
                  </span>
                  <span className="text-sm text-muted-foreground">Hiyerarşi ve yapıyı görüntüle</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start text-left"
                  onClick={() => navigate('/system/tenants')}
                >
                  <span className="flex items-center gap-2 font-semibold mb-1">
                    <i className="material-symbols-outlined text-primary-600">settings</i>
                    Tenant Ayarları
                  </span>
                  <span className="text-sm text-muted-foreground">Tenant ve sistem ayarları</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Son Aktiviteler (placeholder) */}
          <Card>
            <CardHeader>
              <CardTitle>Son Aktiviteler</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Aktivite geçmişi yakında eklenecek.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
